import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Users, Calendar, PlusCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

export default function CommunitySidebar() {
  const { t } = useTranslation("community");
  const { organization } = useOrganization();

  // Trending posts (top liked this week)
  const { data: trendingPosts = [] } = useQuery({
    queryKey: ["community-trending", organization?.id],
    queryFn: async () => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const { data } = await supabase
        .from("community_posts" as any)
        .select("id, content, likes_count, author_id")
        .eq("organization_id", organization!.id)
        .gte("created_at", weekAgo.toISOString())
        .order("likes_count", { ascending: false })
        .limit(3);
      return (data ?? []) as Array<{
        id: string;
        content: string;
        likes_count: number;
        author_id: string;
      }>;
    },
    enabled: !!organization?.id,
  });

  // Upcoming activities
  const { data: upcomingActivities = [] } = useQuery({
    queryKey: ["community-upcoming-activities", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("activities")
        .select("id, title, start_time, current_participants, max_participants")
        .eq("organization_id", organization!.id)
        .gte("start_time", new Date().toISOString())
        .order("start_time", { ascending: true })
        .limit(3);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  return (
    <div className="space-y-4">
      {/* Club info */}
      {organization && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              {organization.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {organization.address || ""}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Trending */}
      {trendingPosts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t("trending")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {trendingPosts.map((post) => (
              <div key={post.id} className="text-xs">
                <p className="text-foreground line-clamp-2">{post.content}</p>
                <p className="text-muted-foreground">
                  {post.likes_count} {t("likes")}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upcoming activities */}
      {upcomingActivities.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t("common:yourUpcomingActivities", "Upcoming activities")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcomingActivities.map((activity) => (
              <Link
                key={activity.id}
                to={`/activities/${activity.id}`}
                className="block rounded-lg border p-2 hover:bg-muted/50 transition-colors"
              >
                <p className="text-xs font-medium">{activity.title}</p>
                <p className="text-[10px] text-muted-foreground">
                  {format(new Date(activity.start_time), "EEE d MMM HH:mm", {
                    locale: sv,
                  })}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {activity.current_participants}/{activity.max_participants}{" "}
                  {t("common:members", "members")}
                </p>
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Quick links */}
      <Card>
        <CardContent className="p-3 space-y-2">
          <Button variant="outline" size="sm" className="w-full gap-1 text-xs" asChild>
            <Link to="/community">
              <PlusCircle className="h-3.5 w-3.5" />
              {t("newPost")}
            </Link>
          </Button>
          <Button variant="ghost" size="sm" className="w-full gap-1 text-xs" asChild>
            <Link to="/players">
              <Users className="h-3.5 w-3.5" />
              {t("common:nav.players", "Players")}
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
